import { useStyletron } from 'baseui'
import { useTranslation } from 'react-i18next'
import { ProgressBar } from 'baseui/progress-bar'
import { Notification, KIND as NOTIFICATION_KIND } from 'baseui/notification'
import useNettingBatches from './useNettingBatches'

const STATUS_COLORS = {
  pending: '#9e9e9e',
  processing: '#1976d2',
  completed: '#2e7d32',
  finished_with_failures: '#ed6c02',
  cancelled: '#757575',
}

const formatDate = (dateString) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const StatusBadge = ({ status }) => {
  const [css] = useStyletron()
  const { t } = useTranslation('organizationManagement')

  return (
    <span
      className={css({
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#fff',
        backgroundColor: STATUS_COLORS[status] || '#9e9e9e',
      })}
    >
      {t(`netting.batches.status_${status}`, status)}
    </span>
  )
}

const BatchRow = ({ batch }) => {
  const [css] = useStyletron()
  const { t } = useTranslation('organizationManagement')

  const isProcessing = batch.status === 'processing'
  const progress = batch.progress ?? 0
  const totalJobs = batch.total_jobs ?? 0
  const processedJobs = batch.processed_jobs ?? 0
  const failedJobs = batch.failed_jobs ?? 0

  return (
    <div
      className={css({
        padding: '10px 12px',
        borderBottom: '1px solid #eee',
        ':last-child': { borderBottom: 'none' },
      })}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        })}
      >
        <div>
          {batch.name && (
            <span className={css({ fontSize: '13px', fontWeight: 600, color: '#333', marginRight: '8px' })}>
              {batch.name}
            </span>
          )}
          <span className={css({ fontSize: '12px', color: '#888' })}>
            {formatDate(batch.created_at)}
          </span>
        </div>
        <StatusBadge status={batch.status} />
      </div>

      {isProcessing && (
        <div className={css({ marginTop: '6px' })}>
          <ProgressBar
            value={progress}
            overrides={{
              BarProgress: {
                style: { backgroundColor: STATUS_COLORS.processing },
              },
              BarContainer: {
                style: { marginLeft: 0, marginRight: 0 },
              },
            }}
          />
          <span className={css({ fontSize: '12px', color: '#777' })}>
            {progress}% — {processedJobs}/{totalJobs} {t('netting.batches.jobs')}
          </span>
        </div>
      )}

      {!isProcessing && totalJobs > 0 && (
        <div className={css({ marginTop: '4px', fontSize: '12px', color: '#777' })}>
          {processedJobs}/{totalJobs} {t('netting.batches.jobs')}
          {failedJobs > 0 && (
            <span className={css({ color: STATUS_COLORS.finished_with_failures, marginLeft: '6px' })}>
              ({failedJobs} {t('netting.batches.failed_jobs')})
            </span>
          )}
          {batch.finished_at && (
            <span className={css({ marginLeft: '8px' })}>
              — {t('netting.batches.finished_at')} {formatDate(batch.finished_at)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

const NettingBatchList = () => {
  const [css] = useStyletron()
  const { t } = useTranslation('organizationManagement')
  const { batches, batchErrors, dismissBatchError } = useNettingBatches()

  return (
    <div className={css({ marginTop: '1.5rem' })}>
      {batchErrors.length > 0 && (
        <div className={css({ marginBottom: '1rem' })}>
          {batchErrors.map((error, index) => (
            <Notification
              key={`${error.reason}-${error.timestamp}`}
              kind={NOTIFICATION_KIND.negative}
              closeable
              onClose={() => dismissBatchError(index)}
              overrides={{
                Body: { style: { width: '100%', marginLeft: 0, marginBottom: '8px' } },
              }}
            >
              {error.message}
            </Notification>
          ))}
        </div>
      )}

      {(!batches || batches.length === 0) ? (
        <div className={css({ color: '#999', fontSize: '14px' })}>
          {t('netting.batches.empty')}
        </div>
      ) : (
        <>
          <h3
            className={css({
              fontSize: '15px',
              fontWeight: 600,
              marginBottom: '8px',
              color: '#333',
            })}
          >
            {t('netting.batches.title')}
          </h3>
          <div
            className={css({
              border: '1px solid #e0e0e0',
              borderRadius: '6px',
              overflow: 'hidden',
            })}
          >
            {batches.map(batch => (
              <BatchRow key={batch.id} batch={batch} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default NettingBatchList
